import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { SubscriptionDisplayType } from 'api/generated/types.gen';

/**
 * Per-`subscriptionDisplayType` message variants for the subscription flow.
 *
 * The noun used for the items a buyer purchases is configured in Box Office
 * (Subscription / Season Card / Season Tickets / Package). Pick the right
 * variant with {@link byDisplayType}.
 */
type DisplayTypeVariants = Record<
  NonNullable<SubscriptionDisplayType>,
  MessageDescriptor
>;

/** Selects the message variant for the given display type, defaulting to Subscription. */
export function byDisplayType(
  variants: DisplayTypeVariants,
  displayType?: SubscriptionDisplayType
): MessageDescriptor {
  return variants[displayType ?? 'Subscription'];
}

export const displayTypeMessages = {
  // Singular product name (page title, cart line)
  typeLabel: defineMessages({
    Subscription: {
      id: 'subscription.title',
      defaultMessage: 'Subscription',
    },
    SeasonCard: {
      id: 'subscription.seasonCard',
      defaultMessage: 'Season Card',
    },
    SeasonTickets: {
      id: 'subscription.seasonTickets',
      defaultMessage: 'Season Tickets',
    },
    Package: {
      id: 'subscription.package',
      defaultMessage: 'Package',
    },
  }),
  // Selected-count label: "{count} cards"
  count: defineMessages({
    Subscription: {
      id: 'subscription.chooseEvents.subscriptionCount',
      defaultMessage:
        '{count, plural, one {# subscription} other {# subscriptions}}',
    },
    SeasonCard: {
      id: 'subscription.chooseEvents.cardCount',
      defaultMessage:
        '{count, plural, one {# season card} other {# season cards}}',
    },
    SeasonTickets: {
      id: 'subscription.chooseEvents.ticketCount',
      defaultMessage:
        '{count, plural, one {# season ticket} other {# season tickets}}',
    },
    Package: {
      id: 'subscription.chooseEvents.packageCount',
      defaultMessage: '{count, plural, one {# package} other {# packages}}',
    },
  }),
  // Card-picker dialog title: "Choose cards"
  chooseTitle: defineMessages({
    Subscription: {
      id: 'subscription.chooseCards.titleSubscription',
      defaultMessage: 'Choose subscriptions',
    },
    SeasonCard: {
      id: 'subscription.chooseCards.title',
      defaultMessage: 'Choose season cards',
    },
    SeasonTickets: {
      id: 'subscription.chooseCards.titleTickets',
      defaultMessage: 'Choose season tickets',
    },
    Package: {
      id: 'subscription.chooseCards.titlePackage',
      defaultMessage: 'Choose packages',
    },
  }),
  // Card-picker dialog subtitle / landing-page picker heading
  chooseTypeAndQuantity: defineMessages({
    Subscription: {
      id: 'subscription.chooseCards.subtitleSubscription',
      defaultMessage: 'Choose subscription type and quantity',
    },
    SeasonCard: {
      id: 'subscription.chooseCards.subtitle',
      defaultMessage: 'Choose season card type and quantity',
    },
    SeasonTickets: {
      id: 'subscription.chooseCards.subtitleTickets',
      defaultMessage: 'Choose season ticket type and quantity',
    },
    Package: {
      id: 'subscription.chooseCards.subtitlePackage',
      defaultMessage: 'Choose package type and quantity',
    },
  }),
};
